/**
 * AutomaticEmails — Studio Messaging v1.1, the automatic customer messages
 * surface. (File name kept: the handover is explicit that this screen is
 * retained and extended, not rebuilt.)
 *
 * WORK HANDLE: studio-messaging-v1
 *
 * v1.2 (2026-09-16, operator): ONE PAGE, A SWITCH PER CHANNEL PER MESSAGE.
 *
 * v1.1 tabbed this surface per channel — Email / SMS / WhatsApp as sibling tabs,
 * each with its own grouped list. That put one message (the 24-hour appointment
 * reminder, say) on three different pages, made "is this reminder on anywhere?"
 * a three-click question, and made the tab strip itself grow as channels were
 * discovered. The operator's call: one list, one row per message, and on that
 * row one switch per channel.
 *
 * So the read is unchanged — still `listMessages({ channel })` once per
 * candidate channel (`AUTO_MESSAGE_CHANNELS`: email, sms, whatsapp; push and
 * webhook are delivery transports, not customer-facing channels) — and the
 * MERGE is new: `mergeChannelRows` keys the per-channel rows by message
 * identity (`rowKey`, i.e. the journey moment key or trigger@offset) and hands
 * the view one `MessageGroup` per message carrying up to three server rows.
 *
 * A channel a message genuinely has no row for renders as "not available", not
 * as an off switch: there is nothing to turn on, and a dead toggle would claim
 * otherwise. The channel COLUMNS are the union of what the tenant carries
 * anywhere, so a tenant that never sends WhatsApp sees no WhatsApp column.
 *
 * EDIT stays PER CHANNEL, because the content is per channel. SMS opens the
 * EXISTING composer in its automatic-message SMS mode
 * (`messaging.compose?auto=1&channel=sms`, `AutomaticSmsComposer`): the
 * composer's own editor, token picker (scoped to what this message can resolve),
 * segment budget and live preview, saving `perChannel.sms` alone. Any other
 * channel opens the composer as before. Email carries no control here at all
 * (see `EDITABLE_CHANNELS`). No new editor, and the composer's own
 * `messages` / `flows` / `delivery` tabs stay hidden — it is reached by direct
 * navigation only. Gated on `campaigns.write`; a journey-moment row needs
 * `flows.write` to be SWITCHED but not to be edited, so the edit gate is
 * deliberately the narrower of the two.
 *
 * PER-CHANNEL ROWS vs SHARED MOMENT ROWS. Appointment rows are
 * channel-discriminated: one row per (trigger, channel), each with its own id
 * and its own `enabled`, so the Email and SMS switches are genuinely separate
 * writes. Journey-moment rows (events, queues) arrive TODAY as one row whose
 * channel list covers every channel it sends on; until the next kit (platform
 * JAI-1619 projects one row per (moment, channel)) the merged row shows ONE
 * switch spanning the channels that row names, labelled as such, instead of
 * three switches that would all write the same field. No faked independent
 * switches — `patchRow` still moves every bucket holding that id.
 *
 * A row's own channels are read from a singular `channel` when the projection
 * carries one (post-JAI-1619) and from `channels[]` otherwise, so this file
 * needs no change when the kit lands.
 * STATUS: WIRED to the real typed clients. Route registration: see the note at
 * the bottom of this docblock.
 *
 * Overlay `messages-2026-09-11-leaf-v3` applied and verified on 2026-09-11:
 * receipt `cd-kit-composed/messages-overlay-receipt.json`, 16/16 file hashes
 * re-verified against disk, effective type-surface hash
 * `17e33219e45425ce54e6460250ea06f77f48ea94bb361c210691e2d013ee3529`, kit stamp
 * deliberately unchanged at `kit-delta-2026-09-10-193c5067`. All five shared
 * symbols confirmed present.
 *
 * DATA SOURCE
 * -----------
 * The screen is still a pure function of an injected `MessagesSource`, because
 * that seam is what makes every interaction state reachable in review without
 * a server. `LIVE_SOURCE` is now the default and is the only thing that talks
 * to the network. `UNWIRED_SOURCE` is retained for the state gallery.
 *
 * The list read exhausts pagination BEFORE filtering (§API wiring): the route
 * merges lineage families, sorts globally by updatedAt and paginates AFTER the
 * merge, so the first page is not the default inventory and `total` is not the
 * row count this page wants.
 *
 * Row selection is by lineage and group identity, never a name whitelist:
 *   · `appointment_comms`                          → Appointments
 *   · `journey_moment` with group.id 'events'      → Events
 *   · `journey_moment` with group.id 'queues'      → Queues
 *   · `library` lineage                            → not this page
 *   · moments without a switch of their own        → excluded via the contract's
 *     `studioMessageJourneyMomentSupportsEnabled`, never a local copy of that
 *     list; their customer-facing rows are the compiled appointment_comms rows.
 *   · a row whose `enabled` is ABSENT              → dropped. Absent means "this
 *     lineage has no such switch", explicitly not `false`.
 * Group comes from the server's derived `group`, falling back to the contract's
 * `studioMessageGroupFor`. Server rows with no catalogue entry are KEPT and
 * rendered from their own name/trigger/offsetMinutes — a tenant's extra cadence
 * step must not vanish.
 *
 * CAPABILITY MATRIX (§Toggle behavior)
 *   campaigns.read   — without it: list permission error, no rows, no fake rows
 *   campaigns.write  — without it: every switch disabled + accessible reason
 *   flows.write      — ADDITIONALLY required for journey-moment switches only;
 *                      appointment-comms switches do not need it
 *   settings.read    — without it: sender card read blocked
 *   settings.write   — without it: both editable sender fields disabled,
 *                      resolved address stays visibly read-only
 * Capability failures are a separate branch from transport failures and are
 * never retryable.
 *
 * VALIDATION is the shared contract schema — `MessagingSenderSettingsPatchInput`
 * from `@v0y4ge/api-contracts`. The temporary local checks this file carried
 * while the overlay was outstanding are GONE, not kept alongside it: a
 * duplicated weaker regex is exactly the CR/LF-injection hole the handover
 * warns about. Nothing is trimmed before the schema sees it.
 *
 * TABS UNDER MESSAGES (v1.2, 2026-09-16, operator): `Automatic messages` is one
 * tab holding this whole list; `Settings` holds the sender card. Both are this
 * one component (`view="general" | "settings"`), and each view loads only its
 * own data — opening Settings does not page through the message list, and
 * opening the list does not read settings the operator may not have access to.
 * The `channel` route param v1.1 used to name the active tab is accepted and
 * ignored, so an old bookmark still lands on the list.
 *
 * JAI-1619, CONFIRMED SHAPE (platform, 2026-09-11). Journey moments are served
 * as one row per (moment, channel) with ids `asset_<key>:email` /
 * `asset_<key>:sms`, each carrying its own `enabled` and its own `channel`; the
 * legacy `asset_<key>` id stays accepted on PATCH as "all channels" but is
 * deprecated. Nothing here changes when that lands: `declaredChannels` reads
 * the singular `channel` first, so a post-kit row has `sharedChannels === null`
 * and the shared-switch note simply stops appearing — the merged row then draws
 * three independent switches because there are three rows — while `patchRow`
 * keeps doing the right thing for any legacy row still projected the old way.
 *
 * ROUTE REGISTRATION — DONE, and here is the judgement call. The handover says
 * register "once the wired screen passes functional acceptance". Functional
 * acceptance needs an operator driving a running app against a live tenant,
 * which cannot happen while the screen is unreachable. So the route and tab are
 * registered to ENABLE that acceptance, not to assert it. Nobody has yet
 * confirmed a successful toggle or sender save against a real backend from this
 * screen, and this file does not claim otherwise. If you would rather it stay
 * dark until accepted, delete the `messaging.automatic` line in
 * shell/screens.tsx and the `automatic` tab in messaging-tabs.ts — the screen
 * keeps working via a direct link.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toggle, Btn, Card, Icon, Input, T, toneColors } from "@v0y4ge/jrni-ui";
import {
  listMessages, updateMessage, getMessagingSettings, updateMessagingSettings, apiRaw,
} from "@v0y4ge/api-client";
import {
  MessagingSenderSettingsPatchInput,
  type MessagingSenderSettings,
  studioMessageGroupFor,
  studioMessageJourneyMomentKeyFromAssetId,
  studioMessageJourneyMomentSupportsEnabled,
  type StudioMessage,
} from "@v0y4ge/api-contracts";
import { Screen, PageHeader } from "../../shell/chrome";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import {
  messagingTabs, useMessagingCounts, AUTO_MESSAGE_CHANNELS, isAutoMessageChannel,
  type AutoMessageChannel,
} from "./messaging-tabs";
import { useT } from "../../lib/i18n";
import { studioRequestOptions } from "../../lib/studio-config";

/* ────────────────────────────────────────────────────────────────────────────
   VIEW TYPES — mapped FROM the server projection by `toMessageRow`.
   These describe what a row RENDERS, not what the API returns.
   ──────────────────────────────────────────────────────────────────────────── */

export type RowLineage = "appointment_comms" | "journey_moment";
export type GroupId = "appointments" | "events" | "queues";
/** The customer-facing channels this surface tabs over. */
export type MessageChannel = AutoMessageChannel;

export interface MessageRow {
  /** Server id. For appointment rows this is the compiler's `mast_*` asset id. */
  id: string;
  /** Server-provided display name. Never a client-side constant. */
  name: string;
  lineage: RowLineage;
  groupId: GroupId;
  /** Server trigger, e.g. `appointment.reminder`, `queue.nearly_up`. */
  trigger: string;
  /** Nullable. Present on appointment cadence steps; identifies the step. */
  offsetMinutes: number | null;
  /** Journey-moment key where the lineage provides one — used for copy only. */
  momentKey: string | null;
  /** The tab this row was read for. */
  channel: MessageChannel;
  /**
   * Set ONLY when this one row is the switch for more than one channel — the
   * pre-JAI-1619 journey-moment shape. `null` means the row owns its channel
   * alone, which is what every appointment row and every post-kit row is.
   */
  sharedChannels: MessageChannel[] | null;
  enabled: boolean;
}

export interface SenderView {
  fromName: string | null;
  replyTo: string | null;
  /** Resolved, READ-ONLY. `null` renders "Not available" — never a guess. */
  fromAddress: string | null;
  fromAddressSource: "platform" | "tenant_credential" | null;
}

/** Rows per channel, in `AUTO_MESSAGE_CHANNELS` order. A channel absent from
 *  the map has no rows and therefore no tab. */
export type ChannelRows = Partial<Record<MessageChannel, MessageRow[]>>;

export interface MessagesSource {
  /** One paginated read per candidate channel; must exhaust pagination. */
  loadRows(): Promise<ChannelRows>;
  /** Resolves with the authoritative server row; rejects with a coded error. */
  setEnabled(id: string, enabled: boolean, channel: MessageChannel): Promise<MessageRow>;
  loadSender(): Promise<SenderView>;
  /** Sends ONLY the field being changed. Never fromAddress/fromAddressSource. */
  saveSender(patch: { fromName?: string | null; replyTo?: string | null }): Promise<SenderView>;
}

/* ────────────────────────────────────────────────────────────────────────────
   LIVE SOURCE
   ──────────────────────────────────────────────────────────────────────────── */

const PAGE_LIMIT = 100;
/** A stop so a paginating read can never spin forever on a bad `hasMore`. */
const MAX_PAGES = 50;

/**
 * Server projection → row, or `null` when the row does not belong on this page.
 * Returning null (rather than throwing) is deliberate, and it is the contract's
 * own stated rule for `journey_moment`: one unrecognised row must not take down
 * the list page it appears on.
 */
/** A row's OWN channels: the singular `channel` a post-JAI-1619 projection
 *  carries, else the `channels[]` every current row carries. */
function declaredChannels(m: StudioMessage): MessageChannel[] {
  const single = (m as { channel?: unknown }).channel;
  if (typeof single === "string" && isAutoMessageChannel(single)) return [single];
  const many: unknown = (m as { channels?: unknown }).channels;
  return Array.isArray(many) ? many.filter(isAutoMessageChannel) : [];
}

/** A row id with any CHANNEL SUFFIX removed.
 *
 *  JAI-1619 addresses a journey moment per channel — `asset_<key>:sms` beside
 *  `asset_<key>` — and `studioMessageJourneyMomentKeyFromAssetId` answers `null`
 *  for the suffixed spelling. Left unstripped that silently disabled everything
 *  downstream of the moment key: the row lost its `momentKey`, fell back to
 *  keying on `trigger`, merged with nothing, and skipped the contract's
 *  no-own-switch exclusion entirely. The suffix is presentation of identity, not
 *  identity, so it comes off before the lookup. */
function assetIdWithoutChannel(id: string): string {
  return String(id).replace(/:(email|sms|whatsapp|push|webhook)$/, "");
}

export function toMessageRow(m: StudioMessage, channel: MessageChannel): MessageRow | null {
  const lineage = m.lineage;
  if (lineage !== "appointment_comms" && lineage !== "journey_moment") return null;

  // The moment a journey row names. `…KeyFromAssetId` is the contract's own
  // reader for the `asset_<key>` ids this surface addresses moments by — read
  // through `assetIdWithoutChannel`, so a channel-addressed row resolves to the
  // same moment as its email sibling and is held to the same exclusions.
  const momentKey = lineage === "journey_moment"
    ? studioMessageJourneyMomentKeyFromAssetId(assetIdWithoutChannel(m.id))
    : null;

  // Moments with no switch of their own are excluded by the CONTRACT's list
  // (STUDIO_MESSAGE_JOURNEY_MOMENTS_WITHOUT_OWN_SWITCH), not by a local copy:
  // appointment_confirmation / appointment_reminder share the appointment plan
  // and their customer-facing rows are the compiled appointment_comms rows.
  if (momentKey && !studioMessageJourneyMomentSupportsEnabled(momentKey)) return null;

  // ABSENT `enabled` means "this lineage has no such switch" — explicitly NOT
  // false. A row with no switch has nothing for this page to render, so it is
  // dropped rather than shown as off.
  if (typeof m.enabled !== "boolean") return null;

  // Group is DERIVED and read-only. Prefer the server's own derivation; fall
  // back to the contract's `studioMessageGroupFor` rather than re-deriving.
  const group = m.group ?? studioMessageGroupFor(lineage, momentKey);
  const groupId = group.id === "appointments" ? "appointments"
    : group.id === "events" ? "events"
      : group.id === "queues" ? "queues"
        : null;
  // An unrecognised journey key groups as `journey_moment` rather than throwing;
  // that floor group is not this page's, so the row is skipped here.
  if (!groupId) return null;

  // `trigger` is appointment_comms-only; a moment row is identified by its key.
  const trigger = m.trigger ?? momentKey;
  if (!trigger) return null;

  // More than one channel on ONE row means one switch drives all of them —
  // the pre-JAI-1619 journey-moment shape, which the row says so about rather
  // than pretending to be per-channel.
  const declared = declaredChannels(m);
  const sharedChannels = declared.length > 1 ? declared : null;

  return {
    id: m.id,
    name: m.name,
    lineage,
    groupId,
    trigger,
    offsetMinutes: m.offsetMinutes ?? null,
    momentKey,
    channel,
    sharedChannels,
    enabled: m.enabled,
  };
}

const LIST_PATH = "/v1/admin/messages";
const listQuery = (channel: MessageChannel, page: number) =>
  `${LIST_PATH}?kind=message&channel=${channel}&lineage=all&page=${page}&limit=${PAGE_LIMIT}`;

async function loadValidatedRows(channel: MessageChannel): Promise<StudioMessage[]> {
  const collected: StudioMessage[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const res = await listMessages({
      kind: "message", channel, lineage: "all", page, limit: PAGE_LIMIT,
    });
    const batch = res.messages;
    collected.push(...batch);
    if (!res.pagination.hasMore || batch.length === 0) break;
  }
  return collected;
}

/**
 * Row-at-a-time read of the SAME endpoint, used only when the validated read
 * rejected the page.
 *
 * `listMessages` validates the list as ONE envelope, so a single stored row
 * carrying a pre-contract value — a legacy `status: "live"`, an `emailDesign`
 * built from the old block vocabulary — fails the parse and the screen loses
 * all fifteen rows to a wall of Zod text. That is exactly the failure
 * `toMessageRow` is written to refuse: one unreadable row must not take down
 * the list page it appears on. Here the envelope is read raw and each row is
 * admitted on its own; a row this page cannot read is skipped and logged, not
 * rendered. A genuine transport or authorization failure still throws, so the
 * error state keeps meaning "the read failed".
 */
async function loadTolerantRows(channel: MessageChannel): Promise<MessageRow[]> {
  const rows: MessageRow[] = [];
  let skipped = 0;
  for (let page = 1; page <= MAX_PAGES; page++) {
    const res = await apiRaw<{
      messages?: unknown[]; data?: unknown[]; pagination?: { hasMore?: boolean };
    }>(listQuery(channel, page), undefined, "listStudioMessages");
    const batch = (res?.messages ?? res?.data ?? []) as unknown[];
    batch.forEach((raw) => {
      const row = rowFromUnknown(raw, channel);
      if (row) rows.push(row); else skipped += 1;
    });
    if (!res?.pagination?.hasMore || batch.length === 0) break;
  }
  if (skipped) console.warn(`automatic messages (${channel}): ${skipped} row(s) this page cannot read were skipped.`);
  return rows;
}

/** `toMessageRow` reads only id/name/lineage/enabled/group/trigger/offsetMinutes —
 *  none of the fields a legacy row fails validation on — so an unvalidated row
 *  can be offered to it behind these guards. */
function rowFromUnknown(raw: unknown, channel: MessageChannel): MessageRow | null {
  if (!raw || typeof raw !== "object") return null;
  const m = raw as Record<string, unknown>;
  if (typeof m.id !== "string" || typeof m.name !== "string") return null;
  try {
    return toMessageRow(m as unknown as StudioMessage, channel);
  } catch (e) {
    console.warn("automatic messages: row skipped as unreadable:", m.id, e);
    return null;
  }
}

async function loadChannelRows(channel: MessageChannel): Promise<MessageRow[]> {
  let collected: StudioMessage[];
  try {
    collected = await loadValidatedRows(channel);
  } catch (e) {
    console.warn(`automatic messages (${channel}): the list failed contract validation; reading rows individually.`, e);
    return loadTolerantRows(channel);
  }
  // Filter AFTER every page is in hand — never treat page 1 as the inventory.
  return collected
    .map((m) => toMessageRow(m, channel))
    .filter((r): r is MessageRow => r !== null);
}

/**
 * One read per candidate channel. A channel that returns no renderable row is
 * left OUT of the map, which is what removes its tab — an empty tab for a
 * channel this tenant does not send on is never shown.
 *
 * The reads run together: they are independent queries and the page needs all
 * of them before it can name its tabs.
 */
async function loadAllChannelRows(): Promise<ChannelRows> {
  const results = await Promise.all(
    AUTO_MESSAGE_CHANNELS.map((channel) => loadChannelRows(channel)),
  );
  const out: ChannelRows = {};
  AUTO_MESSAGE_CHANNELS.forEach((channel, i) => {
    if (results[i].length > 0) out[channel] = results[i];
  });
  return out;
}

function toSenderView(sender: MessagingSenderSettings): SenderView {
  return {
    fromName: sender.fromName,
    replyTo: sender.replyTo,
    fromAddress: sender.fromAddress,
    fromAddressSource: sender.fromAddress ? sender.fromAddressSource : null,
  };
}

export const LIVE_SOURCE: MessagesSource = {
  loadRows: loadAllChannelRows,
  async setEnabled(id, enabled, channel) {
    const updated = await updateMessage(id, { enabled });
    const row = toMessageRow(updated, channel);
    if (!row) throw new Error("The server returned a row this page cannot display.");
    return row;
  },
  async loadSender() {
    // `messaging` is required on the response; `data` is only an optional alias.
    const res = await getMessagingSettings(studioRequestOptions("messaging-automatic"));
    return toSenderView(res.messaging.sender);
  },
  async saveSender(patch) {
    // The PATCH nests `sender` because the GET nests it — the contract is
    // explicit that the two shapes match.
    const res = await updateMessagingSettings({ sender: patch }, studioRequestOptions("messaging-automatic"));
    return toSenderView(res.messaging.sender);
  },
};

export class NotWiredError extends Error {
  code = "MESSAGES_NOT_WIRED";
  constructor() { super("Messages API is not wired in this build."); }
}

/** Retained for the state gallery and for tests that must not hit the network. */
export const UNWIRED_SOURCE: MessagesSource = {
  loadRows: () => Promise.reject(new NotWiredError()),
  setEnabled: () => Promise.reject(new NotWiredError()),
  loadSender: () => Promise.reject(new NotWiredError()),
  saveSender: () => Promise.reject(new NotWiredError()),
};

/* ────────────────────────────────────────────────────────────────────────────
   COPY + TIMING — derived from the server row, behind i18n keys.
   The 15-row table is a CATALOGUE, not a whitelist: `purposeLine` falls back to
   nothing rather than dropping a row, and `timingLine` derives from
   trigger/offsetMinutes so an unknown cadence still prints correct timing.
   ──────────────────────────────────────────────────────────────────────────── */

/** Identity for copy lookup. Appointment steps key on trigger+offset. */
export function rowKey(row: MessageRow): string {
  if (row.lineage === "journey_moment") return row.momentKey ?? row.trigger;
  return row.offsetMinutes === null ? row.trigger : `${row.trigger}@${row.offsetMinutes}`;
}

const PURPOSE: Record<string, [key: string, fallback: string]> = {
  "appointment.confirmed": ["messaging.auto.purpose.apptConfirmed", "Confirms that the appointment was booked."],
  "appointment.reminder@1440": ["messaging.auto.purpose.apptReminder24h", "Reminds the customer about tomorrow's appointment."],
  "appointment.reminder@60": ["messaging.auto.purpose.apptReminder1h", "Gives the customer a last-minute appointment reminder."],
  "appointment.rescheduled": ["messaging.auto.purpose.apptRescheduled", "Tells the customer that the appointment time changed."],
  "appointment.cancelled": ["messaging.auto.purpose.apptCancelled", "Confirms that the appointment was cancelled."],
  "appointment.follow_up@120": ["messaging.auto.purpose.apptFollowUp", "Follows up after the customer's visit."],
  event_confirmation: ["messaging.auto.purpose.eventConfirmation", "Confirms the customer's event place."],
  event_reminder_7d: ["messaging.auto.purpose.eventReminder7d", "Gives the customer time to plan for the event."],
  event_reminder_1d: ["messaging.auto.purpose.eventReminder1d", "Gives the customer a final reminder for tomorrow's event."],
  event_waitlist: ["messaging.auto.purpose.eventWaitlist", "Confirms that a place opened and the waitlisted customer is registered."],
  event_cancelled: ["messaging.auto.purpose.eventCancelled", "Tells the customer that the registered event was cancelled."],
  queue_join: ["messaging.auto.purpose.queueJoin", "Confirms that the customer joined the queue."],
  queue_nearly_up: ["messaging.auto.purpose.queueNearlyUp", "Tells the customer to return because their turn is approaching."],
  queue_called: ["messaging.auto.purpose.queueCalled", "Tells the customer that their turn is ready."],
  queue_delayed: ["messaging.auto.purpose.queueDelayed", "Tells the customer that the queue is delayed while holding their place."],
};

/**
 * Anchor direction. `appointment.follow_up` counts FORWARD from completion;
 * every other offset cadence counts BACKWARD from its anchor. Direction is
 * carried by the trigger, never by the sign of `offsetMinutes`.
 */
function isForward(trigger: string): boolean {
  return trigger.endsWith(".follow_up");
}

function anchorFallback(groupId: GroupId): string {
  return groupId === "events" ? "event" : groupId === "queues" ? "queue" : "appointment";
}

export function timingLine(row: MessageRow, t: (k: string, f: string) => string): string {
  if (row.offsetMinutes === null || row.offsetMinutes === undefined) {
    return t("messaging.auto.timing.onEvent", "When {trigger} occurs.").replace("{trigger}", row.trigger);
  }
  const formatted = new Intl.NumberFormat().format(Math.abs(row.offsetMinutes));
  const anchor = anchorFallback(row.groupId);
  if (isForward(row.trigger)) {
    return t("messaging.auto.timing.after", "{minutes} minutes after {anchor} completion ({trigger}).")
      .replace("{minutes}", formatted).replace("{anchor}", anchor).replace("{trigger}", row.trigger);
  }
  return t("messaging.auto.timing.before", "{minutes} minutes before the {anchor}.")
    .replace("{minutes}", formatted).replace("{anchor}", anchor);
}

export function purposeLine(row: MessageRow, t: (k: string, f: string) => string): string | null {
  const entry = PURPOSE[rowKey(row)];
  // Unknown row: no invented sentence. Name and timing still carry it.
  return entry ? t(entry[0], entry[1]) : null;
}

/* ────────────────────────────────────────────────────────────────────────────
   MERGE — one row per MESSAGE, carrying its channels (v1.2, 2026-09-16)

   The server projects one row per (message, channel); the page shows one row per
   message. `rowKey` is already the message's identity for copy lookup — the
   journey-moment key, or trigger@offset for an appointment cadence step — so it
   is the merge key too, qualified by lineage so a moment and a cadence step can
   never collide.

   Order is FIRST-SEEN order within each group, taken from the channel reads in
   `AUTO_MESSAGE_CHANNELS` order. That means the list is ordered by the email
   read (every tenant's fullest channel) and a message that exists only on SMS
   appends after it, rather than the order changing with whichever read returned
   first.
   ──────────────────────────────────────────────────────────────────────────── */

export interface MessageGroup {
  /** `lineage:rowKey` — stable across channels and across reloads. */
  key: string;
  /** Display name. The email variant's name when there is one: the channels
   *  carry their own names and email is the one every tenant has. */
  name: string;
  groupId: GroupId;
  lineage: RowLineage;
  /** Any one row, for the copy helpers (purpose/timing are per message). */
  sample: MessageRow;
  byChannel: Partial<Record<MessageChannel, MessageRow>>;
}

export function messageGroupKey(row: MessageRow): string {
  /* THE OFFSET IS PART OF THE IDENTITY (CD docket: "editing one must never
     change either of the others"). `rowKey` is the COPY key and for a journey
     moment it is the moment alone, so the 7-day and 1-day event reminders share
     it — merging on that collapsed two messages into one and gave one of them
     the other's SMS record. The merge key therefore carries `offsetMinutes`
     whenever the row has one, for both lineages. An appointment's `rowKey`
     already ends in `@<offset>`, so it is not appended twice — the key is for
     reading as well as for matching. */
  const key = rowKey(row);
  const offset = row.offsetMinutes === null || row.offsetMinutes === undefined ? "" : `@${row.offsetMinutes}`;
  return `${row.lineage}:${key}${key.endsWith(offset) ? "" : offset}`;
}

export function mergeChannelRows(byChannel: ChannelRows): MessageGroup[] {
  const out = new Map<string, MessageGroup>();
  for (const channel of AUTO_MESSAGE_CHANNELS) {
    for (const row of byChannel[channel] ?? []) {
      const key = messageGroupKey(row);
      const existing = out.get(key);
      if (!existing) {
        out.set(key, {
          key, name: row.name, groupId: row.groupId, lineage: row.lineage,
          sample: row, byChannel: { [channel]: row },
        });
        continue;
      }
      existing.byChannel[channel] = row;
      // Email owns the display name where both exist; see the note above.
      if (channel === "email") existing.name = row.name;
    }
  }
  return [...out.values()];
}

/** Channels HIDDEN from this page (2026-09-16, operator): WhatsApp comes off the
 *  grid for now. Hidden, NOT removed — the read still covers it, the merged rows
 *  still carry it, nothing is switched off, and deleting the entry below is the
 *  whole un-hide. Same posture as `HIDDEN_TABS` in messaging-visibility. */
const HIDDEN_CHANNELS: MessageChannel[] = ["whatsapp"];

/** The channel columns to draw: what this tenant carries ANYWHERE, in
 *  `AUTO_MESSAGE_CHANNELS` order, minus the hidden ones. A channel no message
 *  has a row for gets no column — an all-"not available" column asks the
 *  operator to read nothing. */
export function channelColumns(groups: MessageGroup[]): MessageChannel[] {
  return AUTO_MESSAGE_CHANNELS
    .filter((c) => !HIDDEN_CHANNELS.includes(c))
    .filter((c) => groups.some((g) => g.byChannel[c]));
}

const GROUP_ORDER: GroupId[] = ["appointments", "events", "queues"];

export function channelLabel(channel: MessageChannel, t: (k: string, f: string) => string): string {
  return channel === "sms" ? t("messaging.auto.channel.sms", "SMS")
    : channel === "whatsapp" ? t("messaging.auto.channel.whatsapp", "WhatsApp")
      : t("messaging.auto.channel.email", "Email");
}

/** A journey-moment switch is a plan-binding write and needs flows.write too. */
export function rowWriteCapabilities(row: MessageRow): string[] {
  return row.lineage === "journey_moment" ? ["campaigns.write", "flows.write"] : ["campaigns.write"];
}

/* ────────────────────────────────────────────────────────────────────────────
   SENDER VALIDATION — the SHARED contract schema, nothing local.
   The schema does the raw C0/C1/DEL check before any trim, then trims; '' is a
   documented clear for fromName and invalid for replyTo. We canonicalize ONLY
   after a successful parse, per the handover's worked example.
   ──────────────────────────────────────────────────────────────────────────── */

export type SenderField = "fromName" | "replyTo";

export function checkSenderField(
  field: SenderField,
  raw: string,
): { ok: true; value: string | null } | { ok: false; message: string } {
  const parsed = MessagingSenderSettingsPatchInput.safeParse({ [field]: raw });
  if (!parsed.success) {
    const issue = parsed.error.issues.find((i) => i.path[0] === field) ?? parsed.error.issues[0];
    return { ok: false, message: issue?.message ?? "Invalid value." };
  }
  const value = (parsed.data as Record<string, unknown>)[field];
  if (typeof value !== "string") return { ok: true, value: null };
  // Documented canonicalization: an empty display name IS the null clear.
  if (field === "fromName" && value === "") return { ok: true, value: null };
  return { ok: true, value };
}

/* ────────────────────────────────────────────────────────────────────────────
   PRESENTATION
   ──────────────────────────────────────────────────────────────────────────── */

type ListState =
  | { phase: "loading" }
  | { phase: "ready"; byChannel: ChannelRows }
  | { phase: "error"; message: string }
  | { phase: "denied" };

type RowStatus = { pending?: boolean; error?: string | null };

const sectionTitle: React.CSSProperties = { fontSize: 13.5, fontWeight: 700 };
const label: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase",
  letterSpacing: "0.05em", marginBottom: 6,
};

function GroupIcon({ group }: { group: GroupId }) {
  const glyph = group === "appointments" ? <Icon.calendar size={15} />
    : group === "events" ? <Icon.users size={15} />
      : <Icon.clock size={15} />;
  return <span style={{ display: "inline-flex", color: T.p1 }}>{glyph}</span>;
}

/** Column width for a channel cell — wide enough for the widest visible channel
 *  label plus the SMS text control under the switch. */
const CHANNEL_COL = 84;

function ChannelHeader({ columns, t }: { columns: MessageChannel[]; t: (k: string, f: string) => string }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `minmax(0,1fr) repeat(${columns.length}, ${CHANNEL_COL}px)`,
        gap: 14, alignItems: "end", padding: "0 18px 10px",
      }}
    >
      <span />
      {columns.map((channel) => (
        <span key={channel} style={{ ...label, marginBottom: 0, textAlign: "center" }}>
          {channelLabel(channel, t)}
        </span>
      ))}
    </div>
  );
}

/* WHICH CHANNELS CARRY AN EDIT AFFORDANCE, AND IN WHAT FORM (2026-09-16, operator).
 *
 * Not every channel is edited the same way, so the grid stopped drawing the same
 * boxed `Edit` button three times:
 *
 *   · EMAIL  — no edit control here. Email bodies are designed in the composer's
 *     block editor, which is a different job from switching a message on, and a
 *     third boxed button per row for it made the grid unreadable.
 *   · SMS    — editable, but INLINE and quiet: one small pencil control under the
 *     switch (`Icon.edit`, no button chrome), because an SMS is a short text the
 *     operator tweaks in place rather than a layout they compose.
 *   · WHATSAPP — hidden entirely for now (see `HIDDEN_CHANNELS`).
 *
 * Both edits still land in the EXISTING composer with that channel's row id; no
 * new editor, and the capability gate is unchanged (`campaigns.write`).
 */
const EDITABLE_CHANNELS: MessageChannel[] = ["sms"];

/* ui-raw ACCESSIBILITY SHIM — `Toggle` DROPS `aria-label` (2026-09-16).
 *
 * The design-system `Toggle` renders `<span role="switch" aria-checked>` and
 * forwards neither `aria-label` nor `aria-labelledby`, so the label this file
 * passed it never reached the DOM. Under v1.1 that was survivable — one switch
 * per row, named by the row's text, with the channel named by the active tab.
 * After the merge there are three switches per row that differ ONLY by column,
 * so an unnamed switch makes channel identity — the whole point of the redesign
 * — unavailable to a screen reader or voice control.
 *
 * So the name is rendered here, as real (visually hidden) text, and bound to the
 * switch node by `aria-labelledby` after mount. Not `aria-label` on a wrapper:
 * the name has to sit on the element carrying `role="switch"`, and that element
 * belongs to the component. Reverts to a plain `<Toggle aria-label>` the day the
 * design system forwards it — FLAGGED to the design-system line alongside the
 * borderless icon-button request.
 */
function ChannelSwitch({
  row, label: labelText, disabled, onChange,
}: {
  row: MessageRow;
  label: string;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  const host = useRef<HTMLSpanElement | null>(null);
  const labelId = `automail-switch-${row.id}`.replace(/[^a-zA-Z0-9_-]/g, "-");
  useEffect(() => {
    const node = host.current?.querySelector('[role="switch"]');
    if (node) node.setAttribute("aria-labelledby", labelId);
  }, [labelId]);
  return (
    <span ref={host} style={{ display: "inline-flex", alignItems: "center" }}>
      <span
        id={labelId}
        style={{
          position: "absolute", width: 1, height: 1, margin: -1, padding: 0,
          overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
        }}
      >
        {labelText}
      </span>
      <Toggle on={row.enabled} disabled={disabled} onChange={onChange} />
    </span>
  );
}

/**
 * One MESSAGE, with one switch per channel it exists on.
 *
 * A shared moment row (one server row naming several channels, the pre-JAI-1619
 * shape) appears under each channel it names and every copy moves together —
 * they are one write, and the row says so rather than pretending to be three
 * independent switches.
 */
export function MessageGroupRowItem({
  group, columns, statusFor, canWriteChannel, canEdit, missingCapability, onToggle, onRetry, onEdit, t,
}: {
  group: MessageGroup;
  columns: MessageChannel[];
  statusFor: (row: MessageRow) => RowStatus;
  canWriteChannel: (row: MessageRow) => boolean;
  canEdit: boolean;
  missingCapability: (row: MessageRow) => string | null;
  onToggle: (row: MessageRow, next: boolean) => void;
  onRetry: (row: MessageRow) => void;
  onEdit: (row: MessageRow) => void;
  t: (k: string, f: string) => string;
}) {
  const danger = toneColors("danger");
  const purpose = purposeLine(group.sample, t);
  const timing = timingLine(group.sample, t);
  const rows = columns.map((c) => group.byChannel[c]).filter((r): r is MessageRow => !!r);
  const shared = rows.some((r) => r.sharedChannels);
  const saving = rows.some((r) => statusFor(r).pending);
  const failures = rows
    .map((r) => ({ row: r, error: statusFor(r).error }))
    .filter((f): f is { row: MessageRow; error: string } => !!f.error);
  // One reason line for the row: the switches are disabled for the same cause.
  const blocked = rows.map((r) => (canWriteChannel(r) ? null : missingCapability(r))).find((m) => m) ?? null;

  return (
    <div
      data-testid={`automail-row-${group.key}`}
      style={{
        display: "grid",
        gridTemplateColumns: `minmax(0,1fr) repeat(${columns.length}, ${CHANNEL_COL}px)`,
        gap: 14, alignItems: "start", padding: "12px 18px", borderTop: `1px solid ${T.n6}`,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13.5, fontWeight: 650, color: T.n1 }}>{group.name}</span>
          {saving ? (
            <span style={{ fontSize: 11.5, color: T.soft }}>{t("messaging.auto.saving", "Saving…")}</span>
          ) : null}
        </div>
        {purpose ? (
          <div style={{ fontSize: 12.5, color: T.n2, marginTop: 2, lineHeight: 1.5, textWrap: "pretty" }}>{purpose}</div>
        ) : null}
        <div style={{ fontSize: 11.5, color: T.soft, marginTop: 2, lineHeight: 1.5 }}>{timing}</div>
        {shared ? (
          <div style={{ fontSize: 11.5, color: T.soft, marginTop: 4, lineHeight: 1.45 }}>
            {t("messaging.auto.sharedSwitch", "One switch for all channels of this message.")}
          </div>
        ) : null}
        {blocked ? (
          <div style={{ fontSize: 11.5, color: T.soft, marginTop: 4 }}>
            {t("messaging.auto.noWrite", "You do not have permission to change this ({capability}).")
              .replace("{capability}", blocked)}
          </div>
        ) : null}
        {failures.map(({ row, error }) => (
          <div
            key={row.id}
            role="status"
            style={{
              display: "flex", alignItems: "flex-start", gap: 8, marginTop: 8,
              padding: "8px 10px", borderRadius: 8,
              // Inline tinted banner inside a card — the toneColors("danger")
              // role, not the page-level dangerSurface trio.
              background: danger.bg, border: `1px solid ${danger.border}`,
              fontSize: 12, color: danger.fg, lineHeight: 1.45,
            }}
          >
            <span style={{ flex: 1 }}>
              <b style={{ fontWeight: 650 }}>{channelLabel(row.channel, t)}</b> — {error}
            </span>
            <Btn size="sm" variant="secondary" onClick={() => onRetry(row)}>
              {t("messaging.auto.retry", "Retry")}
            </Btn>
          </div>
        ))}
      </div>
      {columns.map((channel) => {
        const row = group.byChannel[channel];
        if (!row) {
          return (
            <div
              key={channel}
              style={{ display: "flex", justifyContent: "center", paddingTop: 2, fontSize: 12, color: T.soft }}
              title={t("messaging.auto.channelUnavailable", "Not sent on this channel.")}
            >
              <span aria-label={`${group.name} — ${channelLabel(channel, t)} — ${t("messaging.auto.notAvailable", "not available")}`}>—</span>
            </div>
          );
        }
        const status = statusFor(row);
        const writable = canWriteChannel(row);
        const stateWord = row.enabled ? t("messaging.auto.on", "on") : t("messaging.auto.off", "off");
        return (
          <div key={channel} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, paddingTop: 2 }}>
            <ChannelSwitch
              row={row}
              disabled={!writable || !!status.pending}
              onChange={(next: boolean) => onToggle(row, next)}
              label={`${group.name} — ${channelLabel(row.channel, t)} — ${stateWord}`}
            />
            {canEdit && EDITABLE_CHANNELS.includes(channel) ? (
              /* ui-raw: borderless icon affordance — `IconBtn` always draws a
                 border and `Btn` iconOnly is a filled control square; a quiet
                 inline pencil is the point here. Third site waiting on a ghost
                 icon-button variant. */
              <button
                type="button"
                onClick={() => onEdit(row)}
                title={t("messaging.auto.editSmsText", "Edit SMS text")}
                aria-label={`${t("messaging.auto.editSmsText", "Edit SMS text")} — ${group.name}`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 4px",
                  border: "none", background: "transparent", color: T.p1, cursor: "pointer",
                  font: "inherit", fontSize: 11.5, borderRadius: 5,
                }}
              >
                <Icon.edit size={12} />
                {t("messaging.auto.text", "Text")}
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function SenderCard({
  state, canRead, canWrite, values, errors, saving, onEdit, onBlur, onRetry, t,
}: {
  state: "loading" | "ready" | "error" | "denied";
  canRead: boolean;
  canWrite: boolean;
  values: { fromName: string; replyTo: string; fromAddress: string | null; fromAddressSource: string | null };
  errors: Partial<Record<SenderField, string>>;
  saving: Partial<Record<SenderField, boolean>>;
  onEdit: (field: SenderField, raw: string) => void;
  onBlur: (field: SenderField) => void;
  onRetry: () => void;
  t: (k: string, f: string) => string;
}) {
  const field = (name: SenderField, labelText: string, hint: string, placeholder: string) => (
    <div>
      <div style={label}>{labelText}</div>
      <Input
        value={values[name]}
        disabled={!canWrite}
        placeholder={placeholder}
        aria-label={labelText}
        aria-invalid={errors[name] ? true : undefined}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEdit(name, e.target.value)}
        onBlur={() => onBlur(name)}
        data-testid={`automail-sender-${name}`}
      />
      <div style={{ display: "flex", gap: 8, marginTop: 5, alignItems: "baseline" }}>
        <span style={{ fontSize: 11.5, color: errors[name] ? T.red1 : T.soft, lineHeight: 1.45, flex: 1 }}>
          {errors[name] ?? hint}
        </span>
        {saving[name] ? (
          <span style={{ fontSize: 11, color: T.soft }}>{t("messaging.auto.saving", "Saving…")}</span>
        ) : null}
      </div>
    </div>
  );

  return (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: "14px 18px 12px", display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${T.n6}` }}>
        <span style={{ display: "inline-flex", color: T.p1 }}><Icon.mail size={15} /></span>
        <span style={sectionTitle}>{t("messaging.auto.sender.title", "Sender")}</span>
      </div>
      <div style={{ padding: "16px 18px 18px" }}>
        {!canRead ? (
          <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>
            {t("messaging.auto.sender.denied", "You do not have permission to view sender settings (settings.read).")}
          </div>
        ) : state === "loading" ? (
          <div style={{ fontSize: 12.5, color: T.soft }}>{t("messaging.auto.sender.loading", "Loading sender settings…")}</div>
        ) : state === "error" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, color: T.red1 }}>
            <span style={{ flex: 1 }}>{t("messaging.auto.sender.loadFailed", "Couldn't load sender settings.")}</span>
            <Btn size="sm" variant="secondary" onClick={onRetry}>{t("messaging.auto.retry", "Retry")}</Btn>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 16, maxWidth: 460 }}>
            {field(
              "fromName",
              t("messaging.auto.sender.fromName", "Sender name"),
              t("messaging.auto.sender.fromNameHint", "Shared with your Brand Kit. Clear it to use the default."),
              t("messaging.auto.sender.fromNamePlaceholder", "Your organization"),
            )}
            <div>
              <div style={label}>{t("messaging.auto.sender.fromAddress", "Sending address")}</div>
              <div
                style={{
                  fontSize: 13, color: values.fromAddress ? T.n1 : T.soft,
                  fontFamily: values.fromAddress ? T.fontMono : T.font,
                  padding: "9px 12px", background: T.cardSoft, border: `1px solid ${T.n6}`,
                  borderRadius: 8, wordBreak: "break-all",
                }}
              >
                {values.fromAddress ?? t("messaging.auto.sender.notAvailable", "Not available")}
              </div>
              <div style={{ fontSize: 11.5, color: T.soft, marginTop: 5, lineHeight: 1.45 }}>
                {values.fromAddress
                  ? t("messaging.auto.sender.fromAddressReadOnly", "Set in your verified sending domain — read-only here.")
                  : t("messaging.auto.sender.fromAddressUnresolved", "No sending address could be resolved. Check your verified sending domain.")}
              </div>
            </div>
            {field(
              "replyTo",
              t("messaging.auto.sender.replyTo", "Reply-to address"),
              t("messaging.auto.sender.replyToHint", "Clear it to send no Reply-To header."),
              t("messaging.auto.sender.replyToPlaceholder", "replies@example.com"),
            )}
            {!canWrite ? (
              <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.45 }}>
                {t("messaging.auto.sender.readOnly", "You do not have permission to change sender settings (settings.write).")}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Which half of the Messages surface this mount renders.
 *
 * The two halves are ONE screen, not two: they share the source seam, the
 * capability reads and the loaders. `general` is the whole automatic-messages
 * list (one row per message, one switch per channel); `settings` is the sender
 * card.
 */
export type MessagesView = "general" | "settings";

/** Rows across every channel bucket, so a shared moment row is updated in all
 *  the tabs that show it rather than only the one it was clicked in. */
function patchRow(byChannel: ChannelRows, id: string, patch: (row: MessageRow) => MessageRow): ChannelRows {
  const out: ChannelRows = {};
  for (const channel of AUTO_MESSAGE_CHANNELS) {
    const rows = byChannel[channel];
    if (rows) out[channel] = rows.map((r) => (r.id === id ? patch(r) : r));
  }
  return out;
}

export default function AutomaticEmails({
  source = LIVE_SOURCE, view = "general",
}: { source?: MessagesSource; view?: MessagesView }) {
  const t = useT();
  const nav = useNav();
  const counts = useMessagingCounts();

  const canRead = can("campaigns.read");
  const canCampaignsWrite = can("campaigns.write");
  const canFlowsWrite = can("flows.write");
  const canSettingsRead = can("settings.read");
  const canSettingsWrite = can("settings.write");

  const [list, setList] = useState<ListState>(() => (canRead ? { phase: "loading" } : { phase: "denied" }));
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});

  const load = useCallback(() => {
    if (view !== "general") return;
    if (!canRead) { setList({ phase: "denied" }); return; }
    setList({ phase: "loading" });
    source.loadRows()
      .then((byChannel) => setList({ phase: "ready", byChannel }))
      .catch((e: unknown) => setList({ phase: "error", message: e instanceof Error ? e.message : String(e) }));
  }, [canRead, source, view]);

  useEffect(() => { load(); }, [load]);

  /* ── one list, channels as columns ─────────────────────────────────────
     v1.1's `?channel=` tab param is deliberately NOT read: there is one page
     now, so an old bookmark carrying it lands here and the param is inert
     rather than selecting a tab that no longer exists. */
  const groups = useMemo(() => (list.phase === "ready" ? mergeChannelRows(list.byChannel) : []), [list]);
  const columns = useMemo(() => channelColumns(groups), [groups]);

  /** One in-flight mutation per row (§Toggle behavior: serialize per row). */
  const inFlight = useRef<Set<string>>(new Set());

  const toggle = useCallback((row: MessageRow, next: boolean) => {
    if (inFlight.current.has(row.id)) return;
    inFlight.current.add(row.id);
    const previous = row.enabled;
    setRowStatus((s) => ({ ...s, [row.id]: { pending: true, error: null } }));
    // Optimistic — the CLICKED row only. A shared moment row is one row that
    // appears in several tabs, so every copy of that id moves together.
    setList((cur) => cur.phase === "ready"
      ? { phase: "ready", byChannel: patchRow(cur.byChannel, row.id, (r) => ({ ...r, enabled: next })) }
      : cur);
    source.setEnabled(row.id, next, row.channel)
      .then((server) => {
        // Adopt the server row as authoritative, keeping each bucket's own tab.
        setList((cur) => cur.phase === "ready"
          ? { phase: "ready", byChannel: patchRow(cur.byChannel, row.id, (r) => ({ ...server, channel: r.channel })) }
          : cur);
        setRowStatus((s) => ({ ...s, [row.id]: { pending: false, error: null } }));
      })
      .catch((e: unknown) => {
        // Roll back to the EXACT previous value — never leave the optimistic one.
        setList((cur) => cur.phase === "ready"
          ? { phase: "ready", byChannel: patchRow(cur.byChannel, row.id, (r) => ({ ...r, enabled: previous })) }
          : cur);
        const message = e instanceof Error ? e.message : String(e);
        const conflict = /OWNERSHIP_CONFLICT|PLAN_TEMPLATE_NOT_ACTIVE/.test(message);
        setRowStatus((s) => ({ ...s, [row.id]: { pending: false, error: message } }));
        // A conflict means our view of the world is stale: re-read before retry.
        if (conflict) load();
      })
      .finally(() => { inFlight.current.delete(row.id); });
  }, [source, load]);

  /* ── sender card ───────────────────────────────────────────────────────── */
  const [senderState, setSenderState] = useState<"loading" | "ready" | "error" | "denied">(
    canSettingsRead ? "loading" : "denied",
  );
  const [sender, setSender] = useState<SenderView>({ fromName: null, replyTo: null, fromAddress: null, fromAddressSource: null });
  const [draft, setDraft] = useState<{ fromName: string; replyTo: string }>({ fromName: "", replyTo: "" });
  const [senderErrors, setSenderErrors] = useState<Partial<Record<SenderField, string>>>({});
  const [senderSaving, setSenderSaving] = useState<Partial<Record<SenderField, boolean>>>({});
  /** Card-level sequence: one queue across BOTH fields (§Sender card). */
  const senderSeq = useRef(0);
  /** Fields with an edit newer than the response being reconciled. */
  const dirty = useRef<Set<SenderField>>(new Set());
  const confirmed = useRef<{ fromName: string; replyTo: string }>({ fromName: "", replyTo: "" });

  const loadSender = useCallback(() => {
    if (view !== "settings") return;
    if (!canSettingsRead) { setSenderState("denied"); return; }
    setSenderState("loading");
    source.loadSender()
      .then((s) => {
        setSender(s);
        const next = { fromName: s.fromName ?? "", replyTo: s.replyTo ?? "" };
        setDraft(next);
        confirmed.current = next;
        setSenderState("ready");
      })
      .catch(() => setSenderState("error"));
  }, [canSettingsRead, source, view]);

  useEffect(() => { loadSender(); }, [loadSender]);

  const editSender = (field: SenderField, raw: string) => {
    dirty.current.add(field);
    setDraft((d) => ({ ...d, [field]: raw }));
    if (senderErrors[field]) setSenderErrors((e) => ({ ...e, [field]: undefined }));
  };

  const blurSender = (field: SenderField) => {
    const raw = draft[field];
    if (raw === confirmed.current[field]) { dirty.current.delete(field); return; }
    const checked = checkSenderField(field, raw);
    if (!checked.ok) {
      // Preserve the entered text; bind the error to its field; do not send.
      setSenderErrors((e) => ({ ...e, [field]: checked.message }));
      return;
    }
    const seq = ++senderSeq.current;
    setSenderSaving((s) => ({ ...s, [field]: true }));
    source.saveSender({ [field]: checked.value } as { fromName?: string | null; replyTo?: string | null })
      .then((server) => {
        setSender(server);
        dirty.current.delete(field);
        // Out-of-order guard: apply a response field only when nothing newer is
        // pending for it, so an older response cannot clobber a newer edit to
        // the OTHER field either.
        if (seq === senderSeq.current) {
          setDraft((d) => ({
            fromName: dirty.current.has("fromName") ? d.fromName : server.fromName ?? "",
            replyTo: dirty.current.has("replyTo") ? d.replyTo : server.replyTo ?? "",
          }));
        }
        confirmed.current = {
          ...confirmed.current,
          [field]: field === "fromName" ? server.fromName ?? "" : server.replyTo ?? "",
        };
        setSenderErrors((e) => ({ ...e, [field]: undefined }));
      })
      .catch((e: unknown) => {
        // Keep the unconfirmed input available for correction; show the error.
        setSenderErrors((errs) => ({
          ...errs,
          [field]: e instanceof Error ? e.message : t("messaging.auto.sender.saveFailed", "Couldn't save. Try again."),
        }));
      })
      .finally(() => setSenderSaving((s) => ({ ...s, [field]: false })));
  };

  const grouped = useMemo(() => {
    const map = new Map<GroupId, MessageGroup[]>(GROUP_ORDER.map((g) => [g, []]));
    for (const g of groups) map.get(g.groupId)?.push(g);
    return map;
  }, [groups]);

  const rowCount = groups.length;

  const groupLabel = (g: GroupId) =>
    g === "appointments" ? t("messaging.auto.group.appointments", "Appointments")
      : g === "events" ? t("messaging.auto.group.events", "Events")
        : t("messaging.auto.group.queues", "Queues");

  const isSettings = view === "settings";

  /* ── Edit ────────────────────────────────────────────────────────────────
     No new editor: the existing composer, reached by direct navigation with the
     id of the row BEHIND THE CLICKED CHANNEL — so the composer opens that
     channel's variant. Gated on `campaigns.write`, the same capability as the
     switches; journey-moment rows need `flows.write` to be SWITCHED but not to
     be edited, so this gate is deliberately the narrower one. */
  const canEdit = canCampaignsWrite;
  const edit = useCallback((row: MessageRow) => {
    if (!canCampaignsWrite) return;
    /* The EXISTING composer, in its automatic-message SMS mode (2026-09-16,
       operator + CD docket): `auto=1&channel=sms` opens that row's SMS content
       with the composer's own editor, token picker, segment budget and preview,
       and a save that patches `perChannel.sms` alone. The id is the one the SMS
       list read RETURNED for this row — never constructed, never the email row's,
       and it carries the reminder offset that distinguishes the three reminders.
       Any other channel opens the composer as before. */
    nav.go("messaging.compose", row.channel === "sms"
      ? { id: row.id, channel: "sms", auto: 1 }
      : { id: row.id });
  }, [canCampaignsWrite, nav]);

  return (
    <Screen
      rail="messaging"
      tabs={messagingTabs(nav, view, counts)}
      activeTab={isSettings ? "settings" : "general"}
    >
      <PageHeader
        breadcrumb={null}
        title={isSettings
          ? t("messaging.settings.title", "Message settings")
          : t("messaging.auto.title", "Automatic messages")}
        subtitle={isSettings
          ? t("messaging.settings.subtitle", "Who these emails come from, and where customer replies go.")
          : t("messaging.auto.subtitleV2", "The messages the platform sends customers on its own — around appointments, events and queues. Turn each channel on or off per message.")}
      />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px", display: "flex", flexDirection: "column", gap: 16, maxWidth: 980 }}>
        {isSettings ? (
          <SenderCard
            state={senderState}
            canRead={canSettingsRead}
            canWrite={canSettingsWrite}
            values={{
              fromName: draft.fromName,
              replyTo: draft.replyTo,
              fromAddress: sender.fromAddress,
              fromAddressSource: sender.fromAddressSource,
            }}
            errors={senderErrors}
            saving={senderSaving}
            onEdit={editSender}
            onBlur={blurSender}
            onRetry={loadSender}
            t={t}
          />
        ) : list.phase === "denied" ? (
          <Card style={{ padding: "16px 18px" }}>
            <div style={{ fontSize: 13, color: T.n1, fontWeight: 650, marginBottom: 4 }}>
              {t("messaging.auto.deniedTitle", "You don't have access to automatic messages")}
            </div>
            <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>
              {t("messaging.auto.deniedBody", "Viewing these messages needs the campaigns.read permission. Ask an administrator to grant it.")}
            </div>
          </Card>
        ) : list.phase === "loading" ? (
          <div style={{ fontSize: 13, color: T.soft }}>{t("messaging.auto.loading", "Loading automatic messages…")}</div>
        ) : list.phase === "error" ? (
          <div
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderRadius: 10,
              // Panel-width failure — the dangerSurface trio (ruling 2026-08-19).
              background: T.dangerSurface, border: `1px solid ${T.dangerSurfaceEdge}`,
              color: T.dangerSurfaceInk, fontSize: 13,
            }}
          >
            <span style={{ flex: 1 }}>
              {t("messaging.auto.loadFailed", "Couldn't load automatic messages.")} {list.message}
            </span>
            <Btn size="sm" variant="secondary" onClick={load}>{t("messaging.auto.retry", "Retry")}</Btn>
          </div>
        ) : rowCount === 0 ? (
          <Card style={{ padding: "22px 18px" }}>
            <div style={{ fontSize: 13, color: T.n1, fontWeight: 650, marginBottom: 4 }}>
              {t("messaging.auto.emptyTitle", "No automatic messages yet")}
            </div>
            <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>
              {t("messaging.auto.emptyBody", "This workspace has no automatic customer messages configured.")}
            </div>
          </Card>
        ) : (
          GROUP_ORDER.map((group) => {
            const rows = grouped.get(group) ?? [];
            if (rows.length === 0) return null;
            return (
              <Card key={group} style={{ padding: 0 }}>
                <div style={{ padding: "14px 18px 10px", display: "flex", alignItems: "center", gap: 8 }}>
                  <GroupIcon group={group} />
                  <span style={sectionTitle}>{groupLabel(group)}</span>
                  <span style={{ fontSize: 11.5, color: T.soft }}>· {rows.length}</span>
                </div>
                <ChannelHeader columns={columns} t={t} />
                {rows.map((g) => (
                  <MessageGroupRowItem
                    key={g.key}
                    group={g}
                    columns={columns}
                    statusFor={(row) => rowStatus[row.id] ?? {}}
                    canWriteChannel={(row) => canCampaignsWrite && (row.lineage !== "journey_moment" || canFlowsWrite)}
                    canEdit={canEdit}
                    missingCapability={(row) => (!canCampaignsWrite ? "campaigns.write"
                      : row.lineage === "journey_moment" && !canFlowsWrite ? "flows.write" : null)}
                    onToggle={toggle}
                    onRetry={(r) => toggle(r, !r.enabled)}
                    onEdit={edit}
                    t={t}
                  />
                ))}
              </Card>
            );
          })
        )}
      </div>
    </Screen>
  );
}

/** The Settings tab — same screen, sender half. Registered as its own route. */
export function MessagingSettingsScreen({ source }: { source?: MessagesSource }) {
  return <AutomaticEmails source={source} view="settings" />;
}
